import React, { useCallback, useEffect, useState } from 'react';
import { ExpandableContent } from '@ax2/common/components/ExpandableContent';
import styles from '@/styles/components/expandable/ExpandableQuestion.module.css';
import { QuestionOverlay } from 'components/question-builders/QuestionOverlay';
import ModalButton from '@ax2/common/components/ModalButton';
import QuestionReorder from '../question-reorder/QuestionReorder';
import QuestionSchema from '@ax2/common/model/schemas/QuestionSchema';
import rearrangeIcon from '../../assets/rearrange.svg';
import removeIcon from '../../assets/trash_bin.svg';
import { ActionButton } from '@ax2/common/components/ActionButton';
import { scroller, Events } from 'react-scroll';
import { useSurveyContext } from 'contexts/SurveyContext';
import { saveIcon } from 'rest/shopClient';

export interface ExpandableQuestionData {
  id: string;
  title?: string;
  subtitle?: string;
  state?: 'edited' | 'needsEdit';
  isNew?: boolean; // used for transition
  onNewProcessed?: () => void; // called when transition is complete
  previewContent: JSX.Element;
  expandedContent: JSX.Element;
  isScreener?: boolean;
  removeCompletion: () => void;
  replaceCompletion: () => void;
  swapCompletion: (questions: QuestionSchema[]) => void;
  isExpanded?: boolean;
  onExpand: (state: boolean) => void;
}

const transitionDurationMs = 400;

export const ExpandableQuestion: React.FC<ExpandableQuestionData> = ({
  isNew,
  onNewProcessed,
  ...props
}) => {
  const [
    isRemoveActionOverlayVisible,
    setIsRemoveActionOverlayVisible,
  ] = useState(false);
  const surveySchema = useSurveyContext();
  const [opacityFrom, setOpacityFrom] = useState(isNew ? 0 : 1);
  const [opacityTo, setOpacityTo] = useState(1);
  const [transitionStage, setTransitionStage] = useState<
    'before' | 'start' | 'inProgress' | 'complete'
  >('before');

  const transition = useCallback((from: number, to: number): Promise<void> => {
    return new Promise<void>(resolve => {
      setOpacityFrom(from);
      setOpacityTo(to);
      setTransitionStage('start');
      setTimeout(() => {
        setTransitionStage('inProgress');
        setTimeout(() => {
          setTransitionStage('complete');
          resolve();
        }, transitionDurationMs);
      }, 1);
    });
  }, []);

  useEffect(() => {
    const processIfNew = async () => {
      if (isNew) {
        await transition(0, 1);
        onNewProcessed && onNewProcessed();
      }
    };
    processIfNew();
  }, [onNewProcessed, isNew, transition]);

  const opacityStyle = () => {
    switch (transitionStage) {
      case 'before':
      case 'start':
        return opacityFrom;
      case 'inProgress':
      case 'complete':
        return opacityTo;
    }
  };

  const transitionStyle = () => {
    switch (transitionStage) {
      case 'before':
      case 'start':
        return undefined;
      case 'inProgress':
        return `opacity ${transitionDurationMs}ms`;
      case 'complete':
        return undefined;
    }
  };

  return (
    <div
      id={props.id}
      className={`${styles.expandableQuestion} ${
        isRemoveActionOverlayVisible ? styles.relative : ''
      }`}
      style={{
        opacity: opacityStyle(),
        transition: transitionStyle(),
      }}
    >
      {isRemoveActionOverlayVisible && (
        <QuestionOverlay
          primaryActions={[
            {
              text: 'Cancel',
              onClick: () => setIsRemoveActionOverlayVisible(false),
            },
          ]}
          secondaryActions={[
            {
              text: 'Remove from survey',
              onClick: async () => {
                await transition(1, 0);
                setIsRemoveActionOverlayVisible(false);
                if (surveySchema) {
                  saveIcon(`color3+${surveySchema.id}`, 'false');
                  saveIcon(`color2+${surveySchema.id}`, 'false');
                }
                props.removeCompletion();
              },
            },
            {
              text: props.isScreener
                ? 'Change question'
                : 'Replace with custom question',
              onClick: async () => {
                await transition(1, 0);
                setIsRemoveActionOverlayVisible(false);
                await props.replaceCompletion();
                await transition(0, 1);
              },
            },
          ]}
        />
      )}
      <ExpandableContent
        title={props.title}
        subtitle={props.subtitle}
        tag={
          props.state && (
            <div
              className={
                props.state === 'edited'
                  ? styles.tagEdited
                  : styles.tagNeedsEdit
              }
            >
              <i className="material-icons">
                {props.state === 'edited' ? 'done' : 'error_outline'}
              </i>
              <span>
                {props.state === 'edited'
                  ? 'Edited'
                  : 'This question still needs edits'}
              </span>
            </div>
          )
        }
        expandLabel="Edit"
        reduceLabel="Close"
        actions={[
          <ActionButton
            key="1"
            type="icon"
            icon={{
              source: removeIcon,
              size: 'xnormal',
            }}
            onClick={() => {
              setIsRemoveActionOverlayVisible(true);
            }}
          />,
          <ModalButton
            key="2"
            type="icon"
            icon={{
              source: rearrangeIcon,
            }}
            content={doClose => (
              <QuestionReorder
                isScreener={props.isScreener}
                onComplete={doClose}
              />
            )}
          />,
        ]}
        previewContent={props.previewContent}
        expandedContent={props.expandedContent}
        isExpanded={props.isExpanded}
        onExpand={async isExpanded => {
          return new Promise(resolve => {
            Events.scrollEvent.register('end', to => {
              if (to === props.id) {
                Events.scrollEvent.remove('end');
                resolve();
              }
            });
            scroller.scrollTo(props.id, {
              duration: 400,
              delay: 0,
              smooth: 'easeInOutQuart',
            });
          }).then(() => props.onExpand(isExpanded));
        }}
      />
    </div>
  );
};
